/**
 * usePopstateSync — keep activity store in sync with React Router URL changes
 * that did NOT originate from useActivityNavigator.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decision D1.
 *
 * Three categories of URL change:
 *   (a) navigator self-triggered (open/push/replaceEntry/replaceActivity):
 *       location.state carries __activityId matching the store's top entry.
 *       The store is already correct → SKIP sync (would be a no-op anyway,
 *       but explicit skip avoids spurious reconcile work and any risk of
 *       reading the store mid-mutation).
 *
 *   (b) browser back / iOS swipe-back / hardware back via RR's POP:
 *       useNavigationType() === "POP". Store still believes the popped
 *       entry is on top → reconcile.
 *
 *   (c) direct navigate() / Layout.tsx legacy close paths / link clicks
 *       outside the navigator (PUSH or REPLACE without our metadata):
 *       Store has stale entries → reconcile.
 *
 * Mount this once at the app root (RootLayout). Cost is one ref + one
 * useEffect per route change; the reconcile itself is O(N entries) and
 * a no-op when the stack is empty.
 *
 * IMPORTANT: this hook does NOT call navigate(). RR has already moved the
 * URL by the time this fires; calling navigate() would loop or duplicate
 * history entries.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";

interface ActivityNavMeta {
  __activityId?: string;
  __entryId?: string;
}

export function usePopstateSync(): void {
  const location = useLocation();
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip the very first render — there's nothing to reconcile yet.
    if (lastKeyRef.current === null) {
      lastKeyRef.current = location.key;
      return;
    }
    if (lastKeyRef.current === location.key) return;
    lastKeyRef.current = location.key;

    const stack = useActivityStore.getState();
    if (stack.activities.length === 0) return;

    // Self-triggered navigator call — store is already correct.
    const meta = (location.state ?? null) as ActivityNavMeta | null;
    const topActivity = stack.activities[stack.activities.length - 1];
    const topEntry = topActivity?.entries[topActivity.entries.length - 1];
    if (
      meta?.__activityId !== undefined &&
      meta.__activityId === topActivity.id &&
      meta.__entryId === topEntry?.id
    ) {
      return;
    }

    const url = location.pathname + location.search;
    useActivityStore.getState().reconcileFromUrl(url);
  }, [location.key, location.pathname, location.search, location.state]);
}
